<template>
  <div
    class="base-card"
    role="article"
    :aria-describedby="`content-${id}`"
  >
    <div class="row-1">
      <div class="tags">
        <component
          :is="result.tagLink ? 'a' : 'div'"
          :href="result.tagLink"
          class="tag"
        >
          {{ result.tagName }}
        </component>
        <a
          :href="result.orgLink"
          class="tag"
          >{{ result.orgName }}
        </a>
      </div>
      <div class="likes-section">
        <button
          type="button"
          aria-label="Like"
        >
          <Icon name="lucide:thumbs-up" />
        </button>
        <button
          type="button"
          aria-label="Dislike"
        >
          <Icon name="lucide:thumbs-down" />
        </button>
      </div>
    </div>
    <div class="row-2">
      <div class="image-preview">
        <slot name="preview" />
      </div>
      <div
        :id="`content-${id}`"
        class="content-section"
      >
        <slot name="result-content" />
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  result: {
    type: Object,
    default: () => ({}),
  },
});

const id = useId();
</script>

<style lang="scss" scoped>
@use '../../global/scss/mixins/_breakpoints.scss' as bp;

.base-card {
  --color-grey-100: #e0e0e0;
  --color-gray-200: #d3d3d3;
  --color-grey-500: #949494;
  --color-grey-950: #2d2d2d;
  --color-blue-500: #1850c1;
  --color-text-primary: #2d2d2d;
  --color-text-secondary: #474747;
  --color-text-tertiary: var(--color-grey-500);
  --color-purple-500: #662e80;

  border-radius: 4px;
  border: 1px solid var(--color-grey-100);
  padding: 16px;
  text-decoration: none;
  gap: 32px;

  &:hover {
    box-shadow:
      0px 4px 6px -1px rgba(0, 0, 0, 0.1),
      0px 0px 5px 0px rgba(0, 0, 0, 0.12);
  }

  &:has(:focus-visible) {
    border: var(--color-purple-500) solid 1px;
    outline: var(--color-blue-500) solid 1px;
    outline-offset: 2px;
    border-radius: 8px;
  }

  a,
  button {
    &:focus-visible {
      outline: var(--color-blue-500) solid 1px;
      outline-offset: 2px;
    }
  }

  .row-1 {
    display: flex;
    justify-content: space-between;
  }

  .row-2 {
    display: flex;
  }

  .image-preview {
    display: flex;
    align-items: center;
    margin-right: 16px;
  }

  .content-section {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .likes-section {
    color: var(--color-grey-500);
    display: flex;
    gap: 12px;

    button {
      border-radius: 4px;
      background: none;
      border: none;
      cursor: pointer;
      color: inherit;
      width: 24px;
      height: 24px;
      &:hover {
        color: var(--color-grey-950);
      }
    }
  }

  :deep(.preview-image),
  :deep(.preview-fallback) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 113px;
    aspect-ratio: 4/3;
    border-radius: 4px;
    background-color: var(--background-color);
    color: var(--text-color);

    img {
      width: 100%;
      aspect-ratio: inherit;
      object-fit: cover;
      border-radius: inherit;
    }

    @include bp.screen('<=sm') {
      width: 96px;
      height: 72px;
      aspect-ratio: 4/3;
    }
  }

  :deep(.title) {
    color: var(--color-text-primary);
    font-family: 'Inter';
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%;
    letter-spacing: 0.8px;
    text-decoration: none;
  }

  :deep(.snippet) {
    color: var(--color-text-secondary);
    font-family: 'Inter';
    font-size: 14px;
    font-weight: 400;
    line-height: 19px;
    letter-spacing: 0.5px;
    margin-top: 4px;
  }

  :deep(.meta-info) {
    display: flex;
    gap: 24px;
    color: var(--color-text-secondary);
    font-family: 'Inter';
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0.1px;
    margin-top: 12px;
    .date,
    .location {
      display: flex;
      align-items: center;
      gap: 8px;
      text-decoration: none;
      color: var(--color-text-secondary);
      font-size: 14px;
      font-weight: 400;
      line-height: 19px;
      letter-spacing: 0.5px;
    }
    span {
      color: var(--color-text-primary);
    }
  }

  :deep(.dates-and-times) {
    color: var(--color-text-tertiary);
    font-family: 'Inter';
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0.1px;
    margin-top: 12px;
  }

  .tags {
    display: flex;
    gap: 8px;
    font-size: 12px;
    font-weight: 450;
    line-height: 16px;
    letter-spacing: 0.5px;
    text-decoration: none;
    margin-bottom: 12px;
    .tag {
      display: inline-flex;
      padding: 4px 8px;
      justify-content: center;
      align-items: center;
      border-radius: 4px;
      background: var(--color-gray-200);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-decoration: none;
      color: var(--color-text-secondary);
      @include bp.screen('<=sm') {
        padding: 2px 7px;
      }
    }
  }
}

@include bp.screen('<=sm') {
  .base-card {
    gap: 16px;
  }

  .tag {
    padding: 2px 7px;
  }
}
</style>
